import Button from '@mui/material/Button';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { handleUnauthorizedResponse } from '../../api/http';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { ErrorContext, ErrorData } from '../../context/ErrorProvider';
import { PolicyContext, PolicyData } from '../../context/policy';
import {
	CLAIM_OPEN_BUTTON_TEXT,
	CLAIM_OPEN_FAILURE_TOAST_MSG,
	CLAIM_OPEN_MODAL_CANCEL_ACTION_TEXT,
	CLAIM_OPEN_MODAL_CONFIRM_ACTION_TEXT,
	CLAIM_OPEN_MODAL_WARNING_MESSAGE,
	CLAIM_OPEN_MODAL_WARNING_TITLE,
	CLAIM_OPEN_SUCCESS_TOAST_MSG,
} from '../../data/labelsAndMessages';
import DefaultDialog from '../DefaultDialog';
import { DATE_PRECISION } from '../fastForm/data/constants';
import { dateConversion } from '../fastForm/utils';
import { successToast } from '../Toast';

interface Props {
	authData: AuthData;
	claimData: ClaimData;
	policyData: PolicyData;
	errorData: ErrorData;
}

const OpenClaimModal = (props: Props) => {
	const { getValues, formState, reset, control } = useFormContext();
	const { dirtyFields } = formState;
	const { authData, claimData, policyData, errorData } = props;
	const {
		claimUpdateReq,
		claim,
		setClaimUpdateReq,
		setEditMode,
		saveFastForm,
	} = claimData;
	const timezone = authData.auth.timezone;
	const { policy } = policyData;
	const [isOpen, setIsOpen] = React.useState(false);
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);

	const formIncidentDateString = useWatch({
		control,
		name: 'claim.incidentDate',
	});

	const incidentTimestamp =
		dateConversion.convertDateStringToZonedTimestamp(
			formIncidentDateString,
			DATE_PRECISION.DAY,
			timezone,
		) || claim.incidentTimestamp;
	const { originalContractStartTimestamp, effectiveContractEndTimestamp } =
		policy;

	const originalContractStartDate = dateConversion.convertTimestampToZonedDayJS(
		originalContractStartTimestamp,
		timezone,
	);
	const effectiveContractEndDate = dateConversion.convertTimestampToZonedDayJS(
		effectiveContractEndTimestamp,
		timezone,
	);
	const incidentDate = dateConversion.convertTimestampToZonedDayJS(
		incidentTimestamp,
		timezone,
	);

	const shouldOpenModal =
		incidentDate.tz() < originalContractStartDate.tz() ||
		incidentDate.tz() > effectiveContractEndDate.tz();

	const openClaim = async () => {
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				openClaim: true,
			});
			setClaimUpdateReq({});
			setEditMode(false);
			successToast(CLAIM_OPEN_SUCCESS_TOAST_MSG());
		} catch (error) {
			errorData.handleErrorResponse(error, CLAIM_OPEN_FAILURE_TOAST_MSG());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			// if update failed, remove status of 'open'
			delete claimUpdateReq.status;
		}
		// Do not perform a state update on a non-mounted component
		shouldOpenModal && close();
	};

	const openModal = () => {
		if (shouldOpenModal) {
			open();
		} else {
			openClaim();
		}
	};

	const renderContent = () => {
		return <div>{CLAIM_OPEN_MODAL_WARNING_MESSAGE()}</div>;
	};

	return (
		<>
			<Button
				key={'close'}
				color='primary'
				onClick={openModal}
				variant='contained'
			>
				{CLAIM_OPEN_BUTTON_TEXT()}
			</Button>
			<DefaultDialog
				actions={[
					{ text: CLAIM_OPEN_MODAL_CONFIRM_ACTION_TEXT(), onClick: openClaim },
					{ text: CLAIM_OPEN_MODAL_CANCEL_ACTION_TEXT(), onClick: close },
				]}
				open={isOpen}
				onClose={close}
				title={CLAIM_OPEN_MODAL_WARNING_TITLE()}
			>
				{renderContent()}
			</DefaultDialog>
		</>
	);
};

function withContext(Component: any) {
	return (
		props: Omit<Props, 'authData' | 'claimData' | 'policyData' | 'errorData'>,
	) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<PolicyContext.Consumer>
									{(policyData) => (
										<Component
											{...props}
											authData={authData}
											claimData={claimData}
											policyData={policyData}
											errorData={errorData}
										/>
									)}
								</PolicyContext.Consumer>
							)}
						</ClaimContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export default withContext(OpenClaimModal);
